import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useIsMutating, useQueryClient } from "react-query";

import { useCreditBuilderDealAction, useStakeCheckingDealAction, useConsumerDealsData } from "../Deals";

import renderManagePaymentMethods from "../../Organisms/Modals/Payment Methods/manage-payment-methods";
import renderWelcomeToStakeSaver from "../../Organisms/Modals/Stake Saver/welcome-to-stake-saver";
import renderCancelStakeSaverModal from "../../Organisms/Modals/cancel-stake-saver-modal";
import renderStakeSaverSetupSuccess from "../../Organisms/Modals/Stake Saver/stake-saver-setup-success";

import { useConsumerPaymentMethods, useDeals } from "../../CustomHooks/Queries";
import { useApplyStakeSaverDiscount, useCancelStakeSaver, useJoinStakeSaver } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import { dispatchAPIErrorToast, dispatchErrorToast, dispatchInfoToast, formatCurrency, getUserId, zPad } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import {
  CardFilled,
  ChevronLeft,
  Discount,
} from "../../Assets/Icons";
import CloseButton from "../../Components/Buttons/close-button";

import "./stake-saver.scss";
import "./stake-saver-responsive.scss";

export default function StakeSaver() {
  const [paymentFrequency, setPaymentFrequency] = useState(null);

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { setAppUtilityProps } = useAppUtilityContext();

  const navigate = useNavigate();
  const location = useLocation();
  const fromWelcome = location.state?.fromWelcome || false;
  const welcomeFlowData = location.state || {};

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.removeItem("fromWelcomeFlow");
      }
    };
  }, []);

  const {
    isLoadingConsumerDealsData,
    stakeSaverOffer,
    stakeCheckingOffer,
    creditBuilderOffer,
    isStakeCheckingActive,
    isCreditBuilderActive,
    consumerDealsAPI,
  } = useConsumerDealsData();

  const { isLoading: isLoadingPaymentMethods, data: paymentMethods } = useConsumerPaymentMethods();

  const [discountCode, setDiscountCode] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedBackupPaymentMethod, setSelectedBackupPaymentMethod] = useState(null);

  const { data: deals } = useDeals();

  const isActivated = stakeSaverOffer?.is_activated;

  useEffect(() => {
    setSelectedPaymentMethod((p) =>
      isActivated
        ? stakeSaverOffer?.check_out_details?.selected_payment_method
        : !!p
        ? p
        : paymentMethods?.data?.data?.[0] ?? null
    );
    setSelectedBackupPaymentMethod(
      isActivated ? stakeSaverOffer?.check_out_details?.selected_backup_payment_method : null
    );
  }, [isActivated, paymentMethods, stakeSaverOffer]);

  const stakeSaverFrequencies = useMemo(
    () =>
      stakeSaverOffer?.check_out_details?.frequency?.map((feeOption) => ({
        ...feeOption,
        label: feeOption.type,
        value: feeOption.type,
      })) ?? [],
    [stakeSaverOffer]
  );

  const filteredFrequency = useMemo(() => {
    if (!paymentFrequency) {
      setPaymentFrequency(stakeSaverFrequencies?.[0]?.type);
    }
    return stakeSaverFrequencies?.find((feeOption) => feeOption.type === paymentFrequency);
  }, [stakeSaverFrequencies, paymentFrequency]);

  const StakeCheckingDealAction = useStakeCheckingDealAction(isStakeCheckingActive);
  const CreditBuilderDealAction = useCreditBuilderDealAction(isCreditBuilderActive);

  const stakeSaverAddOns = useMemo(
    () => [
      ...(!!stakeCheckingOffer ? [{ ...stakeCheckingOffer, onClick: StakeCheckingDealAction }] : []),
      ...(!!creditBuilderOffer ? [{ ...creditBuilderOffer, onClick: CreditBuilderDealAction }] : []),
    ],
    [stakeCheckingOffer, creditBuilderOffer, StakeCheckingDealAction, CreditBuilderDealAction]
  );

  const queryClient = useQueryClient();

  const { isLoading: isJoiningStakeSaver, mutate: JoinStakeSaver } = useJoinStakeSaver({
    onSuccess: async ({ data }) => {
      if (fromWelcome) {
        if (data?.data?.show_alert_message) {
          dispatchInfoToast(data?.data?.alert_message, { key: "joinStakeSaver" });
        }

        await new Promise((resolve) => setTimeout(resolve, 5000));

        await Promise.all([
          queryClient.invalidateQueries(APIQueryKeys.consumerDeals),
          queryClient.invalidateQueries(APIQueryKeys.deals),
          queryClient.invalidateQueries(APIQueryKeys.userAccount),
          queryClient.invalidateQueries(APIQueryKeys.activities),
          queryClient.invalidateQueries(APIQueryKeys.connectedAccounts),
          queryClient.invalidateQueries(APIQueryKeys.stakeDebit),
          queryClient.invalidateQueries(APIQueryKeys.astraCards),
          queryClient.invalidateQueries(APIQueryKeys.payrollAccount),
        ]);

        await Promise.all([
          queryClient.refetchQueries(APIQueryKeys.consumerDeals),
          queryClient.refetchQueries(APIQueryKeys.deals),
          queryClient.refetchQueries(APIQueryKeys.userAccount),
          queryClient.refetchQueries(APIQueryKeys.activities),
          queryClient.refetchQueries(APIQueryKeys.connectedAccounts),
          queryClient.refetchQueries(APIQueryKeys.stakeDebit),
          queryClient.refetchQueries(APIQueryKeys.astraCards),
          queryClient.refetchQueries(APIQueryKeys.payrollAccount),
        ]);

        renderStakeSaverSetupSuccess(modalContext);
        navigate("/home?fromWelcome=true");
      } else {
        renderWelcomeToStakeSaver(modalContext, stakeSaverOffer);
        navigate("/deals/setup-stake-saver");
      }
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "joinStakeSaver" }),
  });

  const { isLoading: isCancelingStakeSaver, mutate: CancelStakeSaver } = useCancelStakeSaver({
    onSuccess: async (cancelStakeSaverResponse) => {
      const consumerDealsResponse = await consumerDealsAPI.refetch();
      if (consumerDealsResponse.isSuccess) {
        const hasAtleastOneOffer = (consumerDealsResponse?.data?.data?.data?.offers?.length ?? 0) > 0;

        if (cancelStakeSaverResponse?.data?.data?.is_individual_signup || hasAtleastOneOffer) {
          navigate("/deals");
        } else {
          setAppUtilityProps((p) => ({
            ...p,
            hideDealsRoutes: true,
          }));
          navigate("/home");
        }
      }
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "cancelStakeSaver" }),
  });

  const handleChangePaymentMethod = (type) =>
    renderManagePaymentMethods(modalContext, {
      selectedPaymentMethod: type === "primary" ? selectedPaymentMethod : selectedBackupPaymentMethod,
      isBackupPayment: type === "backup",
      ...(!isActivated
        ? {
            onChange: (selectedPaymentMethod) => {
              onModalClose();
              if (type === "primary") setSelectedPaymentMethod(selectedPaymentMethod);
              else setSelectedBackupPaymentMethod(selectedPaymentMethod);
            },
          }
        : {
            mustUpdatePaymentMethod: true,
            chargePaymentImmediately: false,
            dealId: stakeSaverOffer?.id,
          }),
    });

  const isWalletOrStakeChecking = selectedPaymentMethod?.is_stake_checking || selectedPaymentMethod?.is_wallet;

  const handleStakeSaverCTAClick = () => {
    if (isActivated) {
      renderCancelStakeSaverModal(modalContext, {
        onCancel: () => {
          onModalClose();
          CancelStakeSaver(deals?.data?.data?.currentDeal?.sfid);
        },
      });
    } else {
      const payload = {
        consumer_sfid: deals?.data?.data?.currentDeal?.sfid,
        frequency: filteredFrequency?.type,
        amount: filteredFrequency?.amount,
        payment_method: selectedPaymentMethod?.is_wallet
          ? "Wallet"
          : selectedPaymentMethod?.is_stake_checking
          ? "Stake Checking"
          : "Card",
        discount_code: discountCode,
        ...(selectedPaymentMethod?.is_card
          ? {
              card_details: {
                card_id: selectedPaymentMethod?.card_id,
              },
            }
          : { backup_card_id: selectedBackupPaymentMethod?.card_id }),
      };

      if (fromWelcome && welcomeFlowData.occupation) {
        payload.occupation = welcomeFlowData.occupation;
      }
      if (fromWelcome && welcomeFlowData.address) {
        payload.address1 = welcomeFlowData.address;
        if (welcomeFlowData.city) payload.city = welcomeFlowData.city;
        if (welcomeFlowData.state) payload.state = welcomeFlowData.state;
        if (welcomeFlowData.zipCode) payload.zipcode = welcomeFlowData.zipCode;
      }
      if (fromWelcome && welcomeFlowData.ssn) {
        payload.ssn = welcomeFlowData.ssn.replace(/\D/g, "");
      }
      if (fromWelcome && welcomeFlowData.dob) {
        payload.dob = welcomeFlowData.dob;
      }

      console.log("join_stake_saver payload:", payload);
      console.log("welcomeFlowData:", welcomeFlowData);
      console.log("fromWelcome:", fromWelcome);

      JoinStakeSaver(payload);
    }
  };

  const isLoading = isLoadingConsumerDealsData || isLoadingPaymentMethods;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  const isApplyingDiscount = useIsMutating(APIQueryKeys.applyingStakeSaverDiscount);

  const [feeWaiverOpen, setFeeWaiverOpen] = useState(false);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState(null);

  const {
    mutate: ApplyDiscount,
    reset: ResetApplyDiscount,
    isSuccess: isDiscountApplied,
    data: discountAppliedData,
  } = useApplyStakeSaverDiscount({
    onSuccess: () => {
      setDiscountCode(appliedDiscountCode);
      setDiscountError(null);
    },
    onError: (err) => setDiscountError(err?.response?.data?.error ?? "Oops... Something went wrong"),
  });

  const checkoutFrequency = useMemo(() => {
    if (isDiscountApplied && discountAppliedData?.data?.data) return discountAppliedData.data.data;
    return filteredFrequency;
  }, [filteredFrequency, isDiscountApplied, discountAppliedData]);

  const isMonthly = paymentFrequency?.toLowerCase() === "monthly";
  const isAnnual = paymentFrequency?.toLowerCase() === "annual";
  const monthlyFreq = stakeSaverFrequencies?.find((f) => f.type?.toLowerCase() === "monthly");
  const annualFreq = stakeSaverFrequencies?.find((f) => f.type?.toLowerCase() === "annual");
  const annualSavings = monthlyFreq && annualFreq ? Math.round(monthlyFreq.amount * 12 - annualFreq.amount) : 0;

  const coFormattedAmount = Math.round(checkoutFrequency?.amount ?? 0);
  const coTypeLabel = isAnnual ? "year" : "month";
  const coTypeSuffix = isAnnual ? "yr" : "mo";
  const coSubTotal = formatCurrency(checkoutFrequency?.sub_total ?? 0);
  const coDiscount = checkoutFrequency?.discount ? formatCurrency(checkoutFrequency.discount * -1) : null;
  const coTotal = formatCurrency(checkoutFrequency?.total ?? 0);

  const handleDiscountApply = () => {
    ApplyDiscount({
      mobile_user_id: getUserId(),
      discount_code: appliedDiscountCode,
      frequency: filteredFrequency?.type?.toLowerCase(),
    });
  };

  const handleDiscountRemove = () => {
    setAppliedDiscountCode("");
    setDiscountCode(null);
    ResetApplyDiscount();
  };

  useEffect(() => {
    if (appliedDiscountCode) {
      ApplyDiscount({
        mobile_user_id: getUserId(),
        discount_code: appliedDiscountCode,
        frequency: filteredFrequency?.type?.toLowerCase(),
      });
    }
    //eslint-disable-next-line
  }, [filteredFrequency?.type]);

  const activatedFrequency = stakeSaverOffer?.check_out_details?.activatedFrequency?.[0];
  const bundleAmount = activatedFrequency?.amount ?? 0;
  const bundleType = activatedFrequency?.type?.toLowerCase() ?? "month";

  const benefits = [
    { title: "$10 Cash Back on Rent", description: "Instant cash back every time you pay rent with Stake" },
    { title: "Credit Building", description: "Report rent to all 3 credit bureaus, boosting your score" },
    { title: "Express PayCheck", description: "Early access to paychecks — get paid up to 2 days early" },
    { title: "Neighborhood Cash", description: "Savings at thousands of national and local brands" },
  ];

  const billingLabel = isWalletOrStakeChecking
    ? `Stake ${selectedPaymentMethod?.is_stake_checking ? "Checking" : "Wallet"}`
    : selectedPaymentMethod?.card_type ?? "";
  const billingLast4 = selectedPaymentMethod?.card_last_4_digits || "";

  if (isActivated) {
    return (
      <div className={`stake-saver ss-settings${animReady ? " anim-ready" : ""}`}>
        <div className="ss-content">
          <div className="ss-page-header">
            <button className="ss-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
              <ChevronLeft />
            </button>
            <span className="ss-page-title">Deals</span>
          </div>

          {isLoading ? (
            <div className="ss-loading">
              <CircularProgress />
            </div>
          ) : (
            <>
              <div className="ss-hero-card">
                <div className="ss-hero-accent" />
                <div className="ss-hero-body">
                  <div className="ss-hero-top">
                    <span className="ss-hero-name">{stakeSaverOffer?.offer_name}</span>
                    <div className="ss-hero-price">
                      <span className="ss-hero-amount">
                        ${Math.round(bundleAmount)}
                      </span>
                      <span className="ss-hero-term">/ {bundleType}</span>
                    </div>
                  </div>
                  <span className="ss-hero-subtitle">
                    Cash back, credit building, and more — all from paying rent.
                  </span>
                </div>
              </div>

              <div className="ss-benefits-section">
                <span className="ss-section-label">Your benefits</span>
                <div className="ss-benefits-list">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="ss-benefit-row">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ss-benefit-check">
                        <circle cx="10" cy="10" r="9" fill="rgba(84,255,153,0.12)" />
                        <path d="M6 10L9 13L14 7" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="ss-benefit-text">
                        <span className="ss-benefit-title">{benefit.title}</span>
                        <span className="ss-benefit-desc">{benefit.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ss-plan-includes-section">
                <span className="ss-section-label">Plan includes</span>
                <div className="ss-plan-pills">
                  {stakeSaverAddOns.map((addOn, idx) => (
                    <div key={idx} className="ss-plan-pill">
                      <div className={`ss-pill-badge ${idx === 0 ? "ss-badge-green" : "ss-badge-blue"}`}>
                        {addOn.title?.charAt(0) ?? (idx === 0 ? "S" : "C")}
                      </div>
                      <span className="ss-pill-name">{addOn.title}</span>
                      <div className="ss-pill-included">
                        <span>Included</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ss-payment-method-card">
                <div className="ss-pm-header">
                  <span>Payment method</span>
                </div>
                <button className="ss-pm-row" onClick={() => handleChangePaymentMethod("primary")}>
                  <div className="ss-pm-left">
                    <div className="ss-pm-icon">
                      {isWalletOrStakeChecking ? (
                        <svg width="12" height="16" viewBox="0 0 69 94" fill="none">
                          <path d="M36.419 93.533C44.487 93.533 52.224 90.333 57.928 84.637C63.632 78.942 66.837 71.217 66.837 63.162C66.837 55.107 63.632 47.382 57.928 41.686C52.224 35.991 44.487 32.791 36.419 32.791V93.533Z" fill="#FFFFFF" />
                          <path d="M32.549 0C24.668 0.272 17.201 3.588 11.722 9.251C6.243 14.914 3.18 22.479 3.18 30.353C3.18 38.227 6.243 45.792 11.722 51.455C17.201 57.117 24.668 60.434 32.549 60.706V0Z" fill="#FFFFFF" />
                          <path d="M0 60.309V60.562C-0.001 69.225 3.414 77.541 9.505 83.711C15.596 89.882 23.875 93.409 32.551 93.533V60.309H0Z" fill="#FFFFFF" />
                          <path d="M36.419 0V29.215H68.681C67.762 21.269 63.984 13.926 58.05 8.553C52.116 3.179 44.429 0.14 36.419 0Z" fill="#54FF99" />
                        </svg>
                      ) : (
                        <CardFilled className="ss-card-icon" />
                      )}
                    </div>
                    <div className="ss-pm-info">
                      <span className="ss-pm-name">{billingLabel}</span>
                      <span className="ss-pm-detail">
                        {isWalletOrStakeChecking
                          ? formatCurrency(selectedPaymentMethod?.current_balance ?? 0)
                          : billingLast4 ? `••••${billingLast4}` : ""}
                      </span>
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ss-pm-chevron">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="#4E5364" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {selectedBackupPaymentMethod && (
                <div className="ss-payment-method-card">
                  <div className="ss-pm-header">
                    <span>Backup payment method</span>
                  </div>
                  <button className="ss-pm-row" onClick={() => handleChangePaymentMethod("backup")}>
                    <div className="ss-pm-left">
                      <div className="ss-pm-icon">
                        <CardFilled className="ss-card-icon" />
                      </div>
                      <div className="ss-pm-info">
                        <span className="ss-pm-name">{selectedBackupPaymentMethod?.card_type}</span>
                        <span className="ss-pm-detail">
                          ....{selectedBackupPaymentMethod?.card_last_4_digits}
                        </span>
                      </div>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ss-pm-chevron">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="#4E5364" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}

              <div className="ss-bottom-actions">
                <button
                  className="ss-cancel-link"
                  onClick={handleStakeSaverCTAClick}
                  disabled={isCancelingStakeSaver}
                >
                  Cancel Stake Saver {isCancelingStakeSaver && <CircularProgress />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const checkoutBenefits = [
    "$10 Cash Back on Rent",
    "Build credit with all 3 major bureaus",
    "Get paid up to 2 days early",
    "Save at local businesses near you (coming soon)",
    "Get 3 months free with discount code STAKESAVER3",
  ];

  const paymentMethodName = !selectedPaymentMethod
    ? "Select a Payment Method"
    : isWalletOrStakeChecking
    ? `Stake ${selectedPaymentMethod?.is_stake_checking ? "Checking" : "Wallet"}`
    : selectedPaymentMethod?.card_type;

  const paymentMethodDetail = selectedPaymentMethod
    ? isWalletOrStakeChecking
      ? formatCurrency(selectedPaymentMethod?.current_balance ?? 0)
      : `••••${selectedPaymentMethod?.card_last_4_digits}, exp ${zPad(selectedPaymentMethod?.exp_month, 2)}/${zPad(
          selectedPaymentMethod?.exp_year % 100,
          2
        )}`
    : null;

  return (
    <div className={`stake-saver ss-checkout${animReady ? " anim-ready" : ""}`}>
      {isLoading ? (
        <div className="ss-loading">
          <CircularProgress />
        </div>
      ) : (
        <div className="ss-checkout-content">
          <div className="ss-page-header">
            <button className="ss-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
              <ChevronLeft />
            </button>
            <span className="ss-page-title">Checkout</span>
          </div>

          {stakeSaverFrequencies.length > 0 && (
            <div className="ss-plan-toggle">
              {stakeSaverFrequencies.map((freq) => (
                <button
                  key={freq.type}
                  className={`ss-toggle-tab${paymentFrequency === freq.type ? " active" : ""}`}
                  onClick={() => setPaymentFrequency(freq.type)}
                >
                  {freq.type}
                </button>
              ))}
            </div>
          )}

          <div className="ss-checkout-hero">
            <div className="ss-checkout-hero-accent" />
            <div className="ss-checkout-hero-body">
              <div className="ss-checkout-hero-header">
                <div className="ss-checkout-hero-left">
                  <span className="ss-checkout-hero-name">
                    {stakeSaverOffer?.offer_name ?? "Stake Saver"}
                  </span>
                  <span className="ss-checkout-hero-badge">Pays for itself</span>
                </div>
                <div className="ss-checkout-hero-right">
                  <div className="ss-checkout-hero-price">
                    <span className="ss-checkout-hero-amount">${coFormattedAmount}</span>
                    <span className="ss-checkout-hero-term">/ {coTypeLabel}</span>
                  </div>
                  {isAnnual && annualSavings > 0 && (
                    <span className="ss-checkout-hero-savings">Save ${annualSavings}</span>
                  )}
                </div>
              </div>

              <p className="ss-checkout-hero-subtitle">
                Turn your rent into cash back, credit, and savings
              </p>

              <div className="ss-fee-waiver-section">
                <button
                  className={`ss-fee-waiver-toggle${feeWaiverOpen ? " open" : ""}`}
                  onClick={() => setFeeWaiverOpen((v) => !v)}
                >
                  {feeWaiverOpen ? (
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M1 2L4 6L7 2" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M2 1L6 4L2 7" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  <span>Monthly fee waived with qualifying activity</span>
                </button>
                {feeWaiverOpen && (
                  <div className="ss-fee-waiver-panel">
                    <p className="ss-fee-waiver-intro">Your $10/mo fee is waived when you:</p>
                    <div className="ss-fee-waiver-conditions">
                      <div className="ss-fee-waiver-condition">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" fill="rgba(84,255,153,0.15)" />
                          <path d="M4.5 7L6.25 8.75L9.5 5.25" stroke="#54FF99" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Pay rent with Stake</span>
                      </div>
                      <div className="ss-fee-waiver-condition">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" fill="rgba(84,255,153,0.15)" />
                          <path d="M4.5 7L6.25 8.75L9.5 5.25" stroke="#54FF99" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Use your Stake card 5+ times</span>
                      </div>
                      <div className="ss-fee-waiver-condition">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6" fill="rgba(84,255,153,0.15)" />
                          <path d="M4.5 7L6.25 8.75L9.5 5.25" stroke="#54FF99" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Get $250+ deposited into your account</span>
                      </div>
                    </div>
                    <p className="ss-fee-waiver-footnote">Most members qualify automatically.</p>
                  </div>
                )}
              </div>

              <div className="ss-checkout-benefits">
                {checkoutBenefits.map((benefit, idx) => (
                  <div key={idx} className="ss-checkout-benefit">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="ss-checkout-benefit-icon">
                      <circle cx="10" cy="10" r="9" fill="rgba(84,255,153,0.12)" />
                      <path d="M6 10L9 13L14 7" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="ss-payment-card">
            <div className="ss-payment-card-header">
              <span>Payment method</span>
            </div>
            <button className="ss-payment-card-method" onClick={() => handleChangePaymentMethod("primary")}>
              <div className="ss-payment-card-icon">
                {isWalletOrStakeChecking ? (
                  <svg width="12" height="16" viewBox="0 0 69 94" fill="none">
                    <path d="M36.419 93.533C44.487 93.533 52.224 90.333 57.928 84.637C63.632 78.942 66.837 71.217 66.837 63.162C66.837 55.107 63.632 47.382 57.928 41.686C52.224 35.991 44.487 32.791 36.419 32.791V93.533Z" fill="#FFFFFF" />
                    <path d="M32.549 0C24.668 0.272 17.201 3.588 11.722 9.251C6.243 14.914 3.18 22.479 3.18 30.353C3.18 38.227 6.243 45.792 11.722 51.455C17.201 57.117 24.668 60.434 32.549 60.706V0Z" fill="#FFFFFF" />
                    <path d="M0 60.309V60.562C-0.001 69.225 3.414 77.541 9.505 83.711C15.596 89.882 23.875 93.409 32.551 93.533V60.309H0Z" fill="#FFFFFF" />
                    <path d="M36.419 0V29.215H68.681C67.762 21.269 63.984 13.926 58.05 8.553C52.116 3.179 44.429 0.14 36.419 0Z" fill="#54FF99" />
                  </svg>
                ) : (
                  <CardFilled className="ss-card-icon" />
                )}
              </div>
              <div className="ss-payment-card-info">
                <span className="ss-payment-card-name">{paymentMethodName}</span>
                {paymentMethodDetail && (
                  <span className="ss-payment-card-detail">{paymentMethodDetail}</span>
                )}
              </div>
              <span className="ss-payment-card-chevron">›</span>
            </button>
            {isWalletOrStakeChecking && !selectedBackupPaymentMethod && (
              <div className="ss-payment-card-backup">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="ss-payment-card-backup-icon">
                  <circle cx="8" cy="8" r="7" stroke="#8B8FA3" strokeWidth="1.5" />
                  <path d="M8 5V8.5" stroke="#8B8FA3" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="8" cy="11" r="0.75" fill="#8B8FA3" />
                </svg>
                <span className="ss-payment-card-backup-text">
                  Add a backup card to avoid interruptions.
                </span>
                <button
                  className="ss-payment-card-backup-action"
                  onClick={() => handleChangePaymentMethod("backup")}
                >
                  Add Card
                </button>
              </div>
            )}
          </div>

          {isWalletOrStakeChecking && selectedBackupPaymentMethod && (
            <div className="ss-payment-card">
              <div className="ss-payment-card-header">
                <span>Backup payment method</span>
              </div>
              <button className="ss-payment-card-method" onClick={() => handleChangePaymentMethod("backup")}>
                <div className="ss-payment-card-icon">
                  <svg width="12" height="16" viewBox="0 0 69 94" fill="none">
                    <path d="M36.419 93.533C44.487 93.533 52.224 90.333 57.928 84.637C63.632 78.942 66.837 71.217 66.837 63.162C66.837 55.107 63.632 47.382 57.928 41.686C52.224 35.991 44.487 32.791 36.419 32.791V93.533Z" fill="#FFFFFF" />
                    <path d="M32.549 0C24.668 0.272 17.201 3.588 11.722 9.251C6.243 14.914 3.18 22.479 3.18 30.353C3.18 38.227 6.243 45.792 11.722 51.455C17.201 57.117 24.668 60.434 32.549 60.706V0Z" fill="#FFFFFF" />
                    <path d="M0 60.309V60.562C-0.001 69.225 3.414 77.541 9.505 83.711C15.596 89.882 23.875 93.409 32.551 93.533V60.309H0Z" fill="#FFFFFF" />
                    <path d="M36.419 0V29.215H68.681C67.762 21.269 63.984 13.926 58.05 8.553C52.116 3.179 44.429 0.14 36.419 0Z" fill="#54FF99" />
                  </svg>
                </div>
                <div className="ss-payment-card-info">
                  <span className="ss-payment-card-name">{selectedBackupPaymentMethod?.card_type}</span>
                  <span className="ss-payment-card-detail">....{selectedBackupPaymentMethod?.card_last_4_digits}</span>
                </div>
                <span className="ss-payment-card-chevron">›</span>
              </button>
            </div>
          )}

          <div className="ss-pricing-card">
            {!isDiscountApplied && (
              <div className="ss-pricing-card-discount">
                <div className="ss-pricing-card-discount-input">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="ss-tag-icon">
                    <path d="M2 4.5C2 3.12 3.12 2 4.5 2H7.17C7.7 2 8.21 2.21 8.59 2.59L13.41 7.41C14.2 8.2 14.2 9.47 13.41 10.24L10.24 13.41C9.46 14.2 8.2 14.2 7.41 13.41L2.59 8.59C2.21 8.21 2 7.7 2 7.17V4.5ZM4.5 5.5C5.05 5.5 5.5 5.05 5.5 4.5C5.5 3.95 5.05 3.5 4.5 3.5C3.95 3.5 3.5 3.95 3.5 4.5C3.5 5.05 3.95 5.5 4.5 5.5Z" fill="#555A6E" />
                  </svg>
                  <input
                    id="ss-discount-code"
                    type="text"
                    placeholder="Discount code"
                    value={appliedDiscountCode}
                    onChange={(e) => { setAppliedDiscountCode(e.target.value); setDiscountError(null); }}
                    onKeyDown={(e) => e.key === "Enter" && handleDiscountApply()}
                    aria-label="Discount code"
                  />
                </div>
                <button className="ss-pricing-card-discount-apply" onClick={handleDiscountApply} disabled={isApplyingDiscount > 0}>
                  {isApplyingDiscount > 0 ? <CircularProgress /> : "Apply"}
                </button>
              </div>
            )}
            {discountError && !isDiscountApplied && (
              <div className="ss-discount-error">{discountError}</div>
            )}
            {isDiscountApplied && (
              <div className="ss-pricing-card-discount">
                <div className="ss-pricing-card-discount-applied">
                  <Discount className="discount-icon" />
                  <span>{appliedDiscountCode}</span>
                  <CloseButton onClick={handleDiscountRemove} className="ss-discount-remove" />
                </div>
              </div>
            )}
            <div className="ss-pricing-card-subtotal">
              <span>Subtotal</span>
              <span>{coSubTotal}</span>
            </div>
            {coDiscount && (
              <div className="ss-pricing-card-subtotal">
                <span>Discount</span>
                <span>{coDiscount}</span>
              </div>
            )}
            <div className="ss-pricing-card-total">
              <span>Total</span>
              <span>{coTotal}/{coTypeSuffix}</span>
            </div>
          </div>

          <div className="ss-checkout-cta">
            <button
              className="ss-checkout-cta-btn"
              onClick={() => {
                const isDisabled =
                  !selectedPaymentMethod ||
                  (isWalletOrStakeChecking && !selectedBackupPaymentMethod) ||
                  isApplyingDiscount > 0 ||
                  isJoiningStakeSaver;

                if (isDisabled) {
                  dispatchErrorToast("Add a backup card to continue.");
                  return;
                }
                handleStakeSaverCTAClick();
              }}
            >
              {isJoiningStakeSaver ? <CircularProgress /> : "Join Stake Saver"}
            </button>
            <p className="ss-checkout-cta-subtitle">No hidden fees. Cancel anytime.</p>
          </div>
        </div>
      )}
    </div>
  );
}
